import {
  type Action,
  type ApprovalRule,
  diffApprovalMatrices,
  diffPermissionMatrices,
  FORM_OFFICE_STEP_NOT_ALLOWED,
  justificationKeyFor,
  type Module,
  nonGrantableReason,
  normalizePermissionCells,
  type PermissionCell,
  parseSubject,
  type Scope,
} from '@felix/domain';
import type { MatrixData, MatrixRoleItem, MatrixSubjects } from './setup-types.js';

/**
 * Matris editörünün saf durum mantığı (ADR 0042 §24) [sorular #249]: hücre döngüsü, kilit ve
 * gerekçe, konu satırları, ızgara klavyesi, onay kuralı formu ve kaydetme formu. Kurallar
 * `@felix/domain`'den; sunucu kaydederken aynı doğrulamayı yeniden yapar.
 */

/** Hücre değeri tıklama ve klavyeyle bu sırayla döner. */
export const SCOPE_CYCLE: readonly Scope[] = ['none', 'own', 'vessel', 'fleet'];

export function nextScope(scope: Scope): Scope {
  return SCOPE_CYCLE[(SCOPE_CYCLE.indexOf(scope) + 1) % SCOPE_CYCLE.length] as Scope;
}

/** Editörün taslağı: iki matris ve ISM formu konu satırları. */
export interface MatrixDraft {
  cells: PermissionCell[];
  justifications: Record<string, string>;
  rules: ApprovalRule[];
  /** Konu satırları (hücresi henüz olmayan eklenmiş konu da görünür). */
  subjects: string[];
}

export function draftFrom(matrix: {
  permission: { cells: PermissionCell[]; justifications: Record<string, string> };
  approval: { rules: ApprovalRule[] };
}): MatrixDraft {
  const cells = normalizePermissionCells(matrix.permission.cells);
  return {
    cells,
    justifications: { ...matrix.permission.justifications },
    rules: [...matrix.approval.rules],
    subjects: [...new Set(cells.map((c) => c.subject).filter((s): s is string => s !== undefined))],
  };
}

const sameCell = (
  cell: PermissionCell,
  roleId: string,
  module: Module,
  action: Action,
  subject: string | undefined,
) =>
  cell.roleId === roleId &&
  cell.module === module &&
  cell.action === action &&
  cell.subject === subject;

export interface CellState {
  scope: Scope;
  /** Hiçbir role verilemez: kuralın kaynağı (ör. `NR496 4.5.3f`). */
  locked: string | null;
  /** Değer verilmişse gerekçe anahtarı (uyarılı hücre). */
  justification: string | null;
}

export function cellState(
  draft: MatrixDraft,
  roles: readonly MatrixRoleItem[],
  roleId: string,
  module: Module,
  action: Action,
  subject?: string,
): CellState {
  const found = draft.cells.find((c) => sameCell(c, roleId, module, action, subject));
  const scope = found?.scope ?? 'none';
  const role = roles.find((r) => r.id === roleId);
  return {
    scope,
    locked: nonGrantableReason(module, action),
    justification: justificationKeyFor(
      { roleId, module, action, scope },
      role ? { id: role.id, pmsResponsible: role.pmsResponsible } : undefined,
    ),
  };
}

export type ScopeResult =
  | { draft: MatrixDraft }
  | { locked: string }
  | { needsJustification: string };

/**
 * Hücreye değer verir. Verilemeyen hücre değişmez (`locked`); uyarılı hücre gerekçesi yoksa
 * gerekçe ister (`needsJustification`; editör pencere açar ve gerekçeyle yeniden çağırır).
 * Modül hücresinde `none` hücreyi kaldırır; konulu hücrede `none` saklanır (modül hücresini
 * kapatır). `none` olan hücrenin gerekçesi silinir.
 */
export function setScope(
  draft: MatrixDraft,
  role: MatrixRoleItem,
  module: Module,
  action: Action,
  subject: string | undefined,
  scope: Scope,
  justification?: string,
): ScopeResult {
  const locked = nonGrantableReason(module, action);
  if (locked && scope !== 'none') return { locked };
  const key = justificationKeyFor(
    { roleId: role.id, module, action, scope },
    { id: role.id, pmsResponsible: role.pmsResponsible },
  );
  const justifications = { ...draft.justifications };
  if (key) {
    const text = justification?.trim() || justifications[key]?.trim();
    if (!text) return { needsJustification: key };
    justifications[key] = text;
  } else {
    const stale = justificationKeyFor(
      { roleId: role.id, module, action, scope: 'own' },
      { id: role.id, pmsResponsible: role.pmsResponsible },
    );
    if (stale && scope === 'none') {
      const others = draft.cells.some(
        (c) =>
          c.roleId === role.id &&
          c.module === module &&
          c.action === action &&
          c.subject !== subject &&
          c.scope !== 'none',
      );
      if (!others) delete justifications[stale];
    }
  }
  const cells = draft.cells.filter((c) => !sameCell(c, role.id, module, action, subject));
  if (scope !== 'none' || subject !== undefined) {
    cells.push({ roleId: role.id, module, action, scope, ...(subject ? { subject } : {}) });
  }
  return { draft: { ...draft, cells: normalizePermissionCells(cells), justifications } };
}

/** Konu satırı ekler (`folder:<anahtar>` ya da `document:<uuid>`); biçim dışı `INVALID_SUBJECT`. */
export function addSubject(draft: MatrixDraft, subject: string): MatrixDraft {
  parseSubject(subject);
  if (draft.subjects.includes(subject)) return draft;
  return { ...draft, subjects: [...draft.subjects, subject] };
}

/** Konu satırını ve hücrelerini kaldırır. */
export function removeSubject(draft: MatrixDraft, subject: string): MatrixDraft {
  return {
    ...draft,
    subjects: draft.subjects.filter((s) => s !== subject),
    cells: draft.cells.filter((c) => c.subject !== subject),
  };
}

/** Taslağın güncel sürüme göre değişiklik sayısı (hücre, gerekçe ve kural). */
export function draftChanges(base: MatrixData, draft: MatrixDraft): number {
  const cells = diffPermissionMatrices(
    {
      version: base.version,
      cells: base.permission.cells,
      justifications: base.permission.justifications,
    },
    { version: base.version, cells: draft.cells, justifications: draft.justifications },
  );
  const rules = diffApprovalMatrices(
    { version: base.version, rules: base.approval.rules },
    { version: base.version, rules: draft.rules },
  );
  return (
    cells.added.length +
    cells.removed.length +
    cells.changed.length +
    cells.justifications.length +
    rules.added.length +
    rules.removed.length +
    rules.changed.length
  );
}

/** Modülde verilmiş (none olmayan) hücre sayısı: modül listesinde gösterilir. */
export function moduleGrants(draft: MatrixDraft, module: Module): number {
  return draft.cells.filter((c) => c.module === module && c.scope !== 'none').length;
}

export interface GridPosition {
  row: number;
  col: number;
}

/**
 * Izgara klavyesi (WAI-ARIA grid): oklar odağı taşır ve kenarda durur, Home/End satırın başı ve
 * sonu; Space ve Enter değeri döndürür (`cycle`). Başka tuş `null`.
 */
export function gridKey(
  key: string,
  position: GridPosition,
  size: { rows: number; cols: number },
): GridPosition | 'cycle' | null {
  const clamp = (value: number, max: number) => Math.max(0, Math.min(max - 1, value));
  switch (key) {
    case ' ':
    case 'Enter':
      return 'cycle';
    case 'ArrowRight':
      return { row: position.row, col: clamp(position.col + 1, size.cols) };
    case 'ArrowLeft':
      return { row: position.row, col: clamp(position.col - 1, size.cols) };
    case 'ArrowDown':
      return { row: clamp(position.row + 1, size.rows), col: position.col };
    case 'ArrowUp':
      return { row: clamp(position.row - 1, size.rows), col: position.col };
    case 'Home':
      return { row: position.row, col: 0 };
    case 'End':
      return { row: position.row, col: size.cols - 1 };
    default:
      return null;
  }
}

export interface SubjectOption {
  value: string;
  label: string;
  group?: string;
}

/** Konu seçicinin seçenekleri: eklenmiş konular çıkarılır; gemi formları gemiye göre gruplanır. */
export function subjectOptions(
  subjects: MatrixSubjects,
  used: readonly string[],
): { folders: SubjectOption[]; documents: SubjectOption[] } {
  return {
    folders: subjects.folders
      .map((folder) => ({
        value: `folder:${folder.key}`,
        label: `${folder.code ? `${folder.code} ` : ''}${folder.name} (${folder.key})`,
      }))
      .filter((option) => !used.includes(option.value)),
    documents: subjects.documents
      .map((doc) => ({
        value: `document:${doc.id}`,
        label: `${doc.docNo ? `${doc.docNo} ` : ''}${doc.title}`,
        ...(doc.vessel ? { group: doc.vessel.name } : {}),
      }))
      .filter((option) => !used.includes(option.value)),
  };
}

// ---------------------------------------------------------------------------------------------
// Onay kuralı formu

export interface RuleForm {
  recordType: string;
  subject: string | null;
  enteredBy: 'any' | string[];
  step1: string | null;
  step1Office: boolean;
  step2: string | null;
  step2Office: boolean;
  minAmount: string;
  maxAmount: string;
  deadlineHours: string;
  selfApprovalReason: string;
}

export const EMPTY_RULE: RuleForm = {
  recordType: 'ism_form',
  subject: null,
  enteredBy: 'any',
  step1: null,
  step1Office: false,
  step2: null,
  step2Office: false,
  minAmount: '',
  maxAmount: '',
  deadlineHours: '',
  selfApprovalReason: '',
};

export function ruleToForm(rule: ApprovalRule): RuleForm {
  return {
    recordType: rule.recordType,
    subject: rule.subject ?? null,
    enteredBy: rule.enteredBy === 'any' ? 'any' : [...rule.enteredBy],
    step1: rule.steps[0] ?? null,
    step1Office: rule.officeSteps?.[0] === true,
    step2: rule.steps[1] ?? null,
    step2Office: rule.officeSteps?.[1] === true,
    minAmount: rule.minAmount === undefined ? '' : String(rule.minAmount),
    maxAmount: rule.maxAmount === undefined ? '' : String(rule.maxAmount),
    deadlineHours: rule.deadlineHours === undefined ? '' : String(rule.deadlineHours),
    selfApprovalReason: rule.selfApprovalReason ?? '',
  };
}

/** Form → kural; en çok iki adım (ADR 0026 §3); konu yalnız ISM formunda (ADR 0042 §9). */
export function formToRule(form: RuleForm): ApprovalRule {
  const steps = [form.step1, form.step1 ? form.step2 : null].filter(
    (id): id is string => typeof id === 'string',
  );
  const rule: ApprovalRule = { recordType: form.recordType, enteredBy: form.enteredBy, steps };
  const office = [form.step1Office, form.step2Office].slice(0, steps.length);
  if (office.some(Boolean)) rule.officeSteps = office;
  if (form.deadlineHours.trim() !== '') rule.deadlineHours = Number(form.deadlineHours);
  if (form.selfApprovalReason.trim() !== '') {
    rule.selfApprovalReason = form.selfApprovalReason.trim();
  }
  if (form.minAmount.trim() !== '') rule.minAmount = Number(form.minAmount);
  if (form.maxAmount.trim() !== '') rule.maxAmount = Number(form.maxAmount);
  if (form.subject && form.recordType === 'ism_form') rule.subject = form.subject;
  return rule;
}

export type RuleFormErrors = Partial<Record<keyof RuleForm, string>>;

export function ruleFormErrors(form: RuleForm): RuleFormErrors {
  const errors: RuleFormErrors = {};
  if (form.subject && form.recordType !== 'ism_form') errors.subject = 'SUBJECT_ONLY_FOR_FORMS';
  if (form.enteredBy !== 'any' && form.enteredBy.length === 0) {
    errors.enteredBy = 'ENTERED_BY_REQUIRED';
  }
  if (!form.step1 && form.step2) errors.step1 = 'REQUIRED';
  if (form.step1Office && !form.step1) errors.step1Office = 'INVALID_OFFICE_STEP';
  if (form.step2Office && !form.step2) errors.step2Office = 'INVALID_OFFICE_STEP';
  // ISM formu ofis onayına gitmez, ofis yalnız görür (sahip kararı 2026-10-17; ADR 0073)
  if (form.recordType === 'ism_form') {
    if (form.step1Office && form.step1) errors.step1Office = FORM_OFFICE_STEP_NOT_ALLOWED;
    if (form.step2Office && form.step2) errors.step2Office = FORM_OFFICE_STEP_NOT_ALLOWED;
  }
  const deadline = form.deadlineHours.trim();
  if (deadline !== '' && !(Number.isInteger(Number(deadline)) && Number(deadline) > 0)) {
    errors.deadlineHours = 'INVALID_DEADLINE';
  }
  const min = form.minAmount.trim();
  const max = form.maxAmount.trim();
  if (min !== '' && !(Number(min) >= 0)) errors.minAmount = 'INVALID_AMOUNT_BAND';
  if (max !== '' && !(Number(max) > (min === '' ? 0 : Number(min)))) {
    errors.maxAmount = 'INVALID_AMOUNT_BAND';
  }
  return errors;
}

/** Kaydetme formu: gerekçe en az 5 karakter, parola zorunlu (ADR 0042 §24). */
export function saveFormErrors(form: { reason: string; password: string }): {
  reason?: string;
  password?: string;
} {
  const errors: { reason?: string; password?: string } = {};
  if (form.reason.trim().length < 5) errors.reason = 'REASON_REQUIRED';
  if (form.password === '') errors.password = 'REAUTHENTICATION_REQUIRED';
  return errors;
}
